import { useState, useEffect, useContext } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Trophy, Users, Clock, Coins, Medal, Crown, Zap,
  ChevronRight, Calendar, Award, Target
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export const TournamentsPage = () => {
  const { user, refreshBalance, token } = useContext(UserContext);
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [tournamentDetails, setTournamentDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    try {
      const response = await axios.get(`${API}/tournaments`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setTournaments(response.data.tournaments || []);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTournamentDetails = async (tournamentId) => {
    try {
      const response = await axios.get(`${API}/tournaments/${tournamentId}`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setTournamentDetails(response.data);
      setSelectedTournament(tournamentId);
    } catch (error) {
      toast.error("Failed to load tournament details");
    }
  };

  const joinTournament = async (tournamentId) => {
    try {
      const response = await axios.post(`${API}/tournaments/${tournamentId}/join`, {}, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      await refreshBalance();
      fetchTournamentDetails(tournamentId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join tournament");
    }
  };

  const formatTimeLeft = (endTime) => {
    const end = new Date(endTime);
    const now = new Date();
    const diff = end - now;
    
    if (diff <= 0) return "Ended";
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h left`;
    return `${hours}h left`;
  };

  const gameIcons = {
    slots: "🎰",
    aviator: "✈️",
    dice: "🎲",
    mines: "💣",
    plinko: "📍",
    wheel: "🎡",
    coinlemon: "🍋",
    crash: "📈",
    limbo: "🎯",
    keno: "🔢"
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#FFD600] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FFD600] to-[#FF6B00] flex items-center justify-center">
            <Trophy className="w-6 h-6 text-black" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white font-['Rajdhani']">Tournaments</h1>
            <p className="text-[#A1A1AA] text-sm">Compete for massive prize pools</p>
          </div>
        </div>

        {/* Active Tournaments Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {tournaments.map((tournament) => (
            <div 
              key={tournament.tournament_id}
              className={`p-5 rounded-2xl border transition-all cursor-pointer hover:scale-[1.02] ${
                selectedTournament === tournament.tournament_id
                  ? 'bg-gradient-to-br from-[#FFD600]/20 to-transparent border-[#FFD600]/50'
                  : 'bg-[#1A1B1F] border-white/10 hover:border-white/20'
              }`}
              onClick={() => fetchTournamentDetails(tournament.tournament_id)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="text-4xl">{gameIcons[tournament.game_type] || "🎮"}</div>
                  <div>
                    <h3 className="text-lg font-bold text-white">{tournament.name}</h3>
                    <p className="text-[#A1A1AA] text-sm">{tournament.description}</p>
                  </div>
                </div>
                <Badge className="bg-[#00E701]/20 text-[#00E701] border-0">
                  {tournament.status}
                </Badge>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1 text-[#FFD600]">
                    <Coins className="w-4 h-4" />
                    ₹{tournament.prize_pool.toLocaleString()}
                  </span>
                  <span className="flex items-center gap-1 text-[#A1A1AA]">
                    <Clock className="w-4 h-4" />
                    {formatTimeLeft(tournament.end_time)}
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  {tournament.entry_fee > 0 && (
                    <span className="text-[#FF6B00] text-sm">₹{tournament.entry_fee} entry</span>
                  )}
                  <ChevronRight className="w-5 h-5 text-white/40" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tournament Details */}
        {tournamentDetails && (
          <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="text-5xl">{gameIcons[tournamentDetails.tournament?.game_type] || "🎮"}</div>
                <div>
                  <h2 className="text-2xl font-bold text-white">{tournamentDetails.tournament?.name}</h2>
                  <p className="text-[#A1A1AA]">{tournamentDetails.tournament?.description}</p>
                </div>
              </div>
              
              {!tournamentDetails.user_participating ? (
                <Button
                  onClick={() => joinTournament(selectedTournament)}
                  className="bg-[#00E701] hover:bg-[#00C201] text-black font-bold px-6"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Join Tournament
                  {tournamentDetails.tournament?.entry_fee > 0 && ` (₹${tournamentDetails.tournament.entry_fee})`}
                </Button>
              ) : (
                <Badge className="bg-[#00E701]/20 text-[#00E701] border-0 px-4 py-2 text-sm">
                  ✓ Participating
                </Badge>
              )}
            </div>

            {/* Prize Distribution */}
            <div className="mb-6">
              <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                <Award className="w-5 h-5 text-[#FFD600]" />
                Prize Distribution
              </h3>
              <div className="grid grid-cols-5 gap-2">
                {tournamentDetails.tournament?.prizes?.map((prize, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-xl text-center ${
                      idx === 0 ? 'bg-[#FFD700]/20 border border-[#FFD700]/30' :
                      idx === 1 ? 'bg-[#C0C0C0]/20 border border-[#C0C0C0]/30' :
                      idx === 2 ? 'bg-[#CD7F32]/20 border border-[#CD7F32]/30' :
                      'bg-white/5 border border-white/10'
                    }`}
                  >
                    <div className="text-lg mb-1">
                      {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                    </div>
                    <p className="text-white font-bold">₹{prize.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Leaderboard */}
            <div>
              <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                <Target className="w-5 h-5 text-[#00E701]" />
                Leaderboard
              </h3>
              
              {tournamentDetails.user_rank && (
                <div className="mb-3 p-3 rounded-xl bg-[#007AFF]/20 border border-[#007AFF]/30">
                  <p className="text-[#007AFF] text-sm">
                    Your Rank: <strong>#{tournamentDetails.user_rank}</strong>
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                {tournamentDetails.leaderboard?.slice(0, 10).map((entry, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center justify-between p-3 rounded-xl ${
                      entry._id === user?.user_id 
                        ? 'bg-[#00E701]/10 border border-[#00E701]/30' 
                        : 'bg-white/5'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        idx === 0 ? 'bg-[#FFD700] text-black' :
                        idx === 1 ? 'bg-[#C0C0C0] text-black' :
                        idx === 2 ? 'bg-[#CD7F32] text-black' :
                        'bg-white/10 text-white'
                      }`}>
                        {idx + 1}
                      </div>
                      <span className="text-white font-mono">
                        {entry._id === user?.user_id ? 'You' : `User ${entry._id?.slice(-4)}`}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-[#FFD600] font-bold">₹{entry.total_wagered?.toLocaleString()}</p>
                      <p className="text-[#A1A1AA] text-xs">{entry.wins} wins</p>
                    </div>
                  </div>
                ))}
                
                {(!tournamentDetails.leaderboard || tournamentDetails.leaderboard.length === 0) && (
                  <p className="text-[#A1A1AA] text-center py-4">
                    No participants yet. Be the first to join!
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* How Tournaments Work */}
        <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10">
          <h3 className="text-lg font-bold text-white mb-4">How Tournaments Work</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-center text-center p-4">
              <div className="w-14 h-14 rounded-full bg-[#00E701]/20 flex items-center justify-center mb-3">
                <Users className="w-6 h-6 text-[#00E701]" />
              </div>
              <h4 className="text-white font-bold mb-1">1. Join Tournament</h4>
              <p className="text-[#A1A1AA] text-sm">
                Pay entry fee (if any) and start competing
              </p>
            </div>
            
            <div className="flex flex-col items-center text-center p-4">
              <div className="w-14 h-14 rounded-full bg-[#FFD600]/20 flex items-center justify-center mb-3">
                <Target className="w-6 h-6 text-[#FFD600]" />
              </div>
              <h4 className="text-white font-bold mb-1">2. Play & Climb</h4>
              <p className="text-[#A1A1AA] text-sm">
                Play the tournament game and climb the leaderboard
              </p>
            </div>
            
            <div className="flex flex-col items-center text-center p-4">
              <div className="w-14 h-14 rounded-full bg-[#FF6B00]/20 flex items-center justify-center mb-3">
                <Trophy className="w-6 h-6 text-[#FF6B00]" />
              </div>
              <h4 className="text-white font-bold mb-1">3. Win Prizes</h4>
              <p className="text-[#A1A1AA] text-sm">
                Top players split the prize pool!
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
